import { create } from 'zustand'

/**
 * As SUGESTOES do turno — os botoes de proximo passo que o motor manda.
 *
 * POR QUE ESTA LOJA EXISTE, medido em 05/09/2026: `chat-stream.ts` ja tratava
 * o evento `suggestions` e chamava `callbacks.onSuggestions`, e uma varredura
 * no repositorio inteiro nao achou NENHUM consumidor desse callback. O motor
 * mandava, o cano entregava, e a tela jogava fora — o usuario nunca via.
 * Feature com zero consumidores e o mesmo defeito que o tronco acabou de
 * consertar no golden do Robson.
 *
 * A loja e do TURNO, nao da conversa: sugestao velha ao lado de resposta nova
 * e pior que sugestao nenhuma, porque convida a pessoa a pedir de novo o que
 * ela ja pediu. Cada turno limpa as anteriores.
 */

export interface Sugestao {
  /**
   * A QUE FAMILIA o chip pertence — e o que faz o painel se explicar sozinho.
   *
   * Denis, 08/09/2026: "tem que morar dentro do icone de indicador ok tem que
   * ser auto explicativo ok". O #801 pendurou duas perguntas de AUDITORIA na
   * mesma lista dos seis de INDICADOR, e o icone (um grafico de barras) passou
   * a abrir oito botoes de dois assuntos diferentes, sem dizer qual e qual.
   * Ninguem quebrou nada: cada mudanca estava certa sozinha, e a soma ficou
   * muda.
   *
   * O grupo e OPCIONAL de proposito: a sugestao de proximo passo que o motor
   * manda em texto livre nao pertence a familia nenhuma, e ganhar um titulo
   * inventado seria pior que nao ter titulo.
   */
  readonly grupo?: 'indicador' | 'auditoria'
  /** O que aparece no botao. */
  readonly label: string
  /** O que e enviado ao chat quando a pessoa clica — prompt pronto, nao rotulo. */
  readonly prompt: string
  readonly icon?: string
  /**
   * O ID DO CHIP no registro de intents do motor (`modules/insights/intents.ts`).
   *
   * Um chip nao e uma frase que precisa ser interpretada — e um botao que o
   * produto desenhou. Mandando o id junto do texto, o motor nao tem de adivinhar
   * o que a pessoa quis: ele sabe, porque foi ele quem ofereceu o botao.
   *
   * MEDIDO em 07/09/2026, duas rodadas contra a base de develop: sem o id, os
   * chips "Corretiva versus preventiva nos ultimos 6 meses" e "Quanto do plano
   * preventivo cumprimos?" viravam PESQUISA DE EQUIPAMENTO na internet — o motor
   * lia o texto do chip como se fosse o nome de uma maquina e voltava 3 a 4
   * minutos depois com uma sugestao de plano para uma maquina chamada "Quanto do
   * plano preventivo cumprimos?".
   *
   * Opcional porque sugestao de proximo passo que o motor gera em texto livre
   * nao tem intent — so os chips do registro tem.
   */
  readonly intentId?: string
  /**
   * STO-29 — botões de sugestão por step no chat.
   *
   * Quando o motor oferece uma ação determinística (ex.: escolha de
   * criticidade, início de execução, encerramento), o clique NÃO deve voltar
   * como texto livre: o `actionId` viaja no canal `onboardingAction`, e o
   * `value` (quando presente) é o texto que aparece na bolha do usuário.
   * `step` e `state` permitem à tela renderizar condicionalmente agrupando
   * os botões pelo passo/estado atual do fluxo.
   */
  readonly actionId?: string
  readonly value?: string
  readonly step?: string
  readonly state?: string
}

interface SugestoesState {
  readonly itens: readonly Sugestao[]
  /**
   * O icone de perguntas prontas do compositor liga isto, e e a UNICA chave que
   * poe os oito chips de partida na tela — dentro ou fora de conversa ja andando
   * (Principio n1 — icone dentro da conversa, nunca pagina). Desligado, nao ha
   * chip de partida em tela nenhuma. Desliga sozinho quando um chip e clicado ou
   * o proximo turno comeca.
   */
  readonly mostrarPartida: boolean
  /** Substitui as do turno anterior. */
  readonly recebe: (itens: readonly Sugestao[]) => void
  readonly limpa: () => void
  readonly alternaPartida: () => void
}

export const useSugestoesStore = create<SugestoesState>((set) => ({
  itens: [],
  mostrarPartida: false,
  recebe: (itens) => {
    set({ itens })
  },
  limpa: () => {
    set({ itens: [], mostrarPartida: false })
  },
  alternaPartida: () => {
    set((s) => ({ mostrarPartida: !s.mostrarPartida }))
  },
}))

/**
 * ═══ OS SEIS DE INDICADOR — a primeira familia do painel ═══
 *
 * O icone de grafico de barras do compositor abre DUAS familias; esta e a
 * primeira. Cada um e uma pergunta que um gestor de manutencao realmente faz, e
 * as seis estao no gabarito de roteamento do motor (`evals/insights.yaml`), com
 * o mesmo texto. Nao e coincidencia nem duplicacao descuidada: o chip so vale
 * se a pergunta dele for a que a bateria mede. Se alguem mudar o texto aqui sem
 * mudar la, o caso de eval passa a medir uma pergunta que ninguem faz.
 *
 * A lista e SEPARADA da de auditoria porque o painel agrupa por familia e o
 * rotulo de cada grupo sai daqui — nao porque as duas morem em telas
 * diferentes. Elas moram na mesma superficie: o icone.
 *
 * POR QUE ESTES ROTULOS NAO VAO PARA O i18n (medido em 08/09/2026)
 *
 * A catraca de texto de tela cobra chave de traducao, e estes oito ficam de
 * fora — nao por preguica, e sim porque traduzi-los QUEBRA o produto:
 *
 *   - `prompt` e o texto que vai para o motor, e o motor PINA a ferramenta a
 *     partir dele (`ehPerguntaDeIndicador`, `ehPerguntaDeAuditoria` — os dois
 *     sao predicados em portugues). Traduzir o `prompt` desliga o pino.
 *   - traduzir so o `label` e manter o `prompt` em portugues faria a pessoa
 *     LER numa lingua e MANDAR noutra: ela clica "Are my data good?" e a bolha
 *     da conversa mostra "Meus dados estao bons?".
 *
 * O conserto de verdade e do lado do motor (predicado por idioma), e nao daqui.
 * Ate la, o par `label === prompt` em pt-BR e a unica forma que nao mente — e
 * ha teste cobrando essa igualdade. Quem acrescentar chip paga a catraca em
 * outro lugar, como este pedido pagou. O ROTULO DO GRUPO, esse sim, e chave de
 * traducao (`chat.chips.grupo.*`): ele e texto de tela, nao vai para o motor.
 */
export const CHIPS_DE_INDICADOR: readonly Sugestao[] = [
  {
    grupo: 'indicador',
    label: 'Como está minha manutenção este mês?',
    prompt: 'Como está minha manutenção este mês?',
    intentId: 'insights.monthly_overview',
  },
  {
    grupo: 'indicador',
    label: 'Top 10 ofensores do trimestre',
    prompt: 'Top 10 ofensores do trimestre',
    intentId: 'insights.top_offenders',
  },
  {
    grupo: 'indicador',
    label: 'Backlog por equipe',
    prompt: 'Backlog por equipe',
    intentId: 'insights.backlog_by_team',
  },
  {
    grupo: 'indicador',
    label: 'Corretiva versus preventiva nos últimos 6 meses',
    prompt: 'Corretiva versus preventiva nos últimos 6 meses',
    intentId: 'insights.mix_by_type_6m',
  },
  {
    grupo: 'indicador',
    label: 'Custo de manutenção por área',
    prompt: 'Custo de manutenção por área',
    intentId: 'insights.cost_by_area',
  },
  {
    grupo: 'indicador',
    label: 'Quanto do plano preventivo cumprimos?',
    prompt: 'Quanto do plano preventivo cumprimos?',
    intentId: 'insights.pm_compliance',
  },
]

/**
 * ═══ AS DUAS DE AUDITORIA — DENTRO DO ÍCONE, NA FAMÍLIA DELAS ═══
 *
 * Denis, 08/09/2026, com foto do composer: "cara tudo precisa ficar dentro do
 * icone colapsado caramba esses dois ficaram pra fora".
 *
 * O #806 tinha mandado estas duas para a tela vazia, por ordem dele. A foto
 * mostra o resultado: as duas flutuando acima do campo numa conversa nova, com
 * o ícone ao lado marcado `aria-pressed={false}`. Voltaram para dentro do
 * ícone, agrupadas sob o rótulo delas.
 *
 * Os ids são os do registro do motor (`modules/insights/intents.ts`,
 * `INTENTS_DE_AUDITORIA`) e os rótulos são os mesmos, letra por letra: o motor
 * pina a ferramenta da auditoria a partir do TEXTO, então rótulo diferente aqui
 * é chip que abre a conversa e não roda auditoria nenhuma.
 */
export const CHIPS_DE_AUDITORIA: readonly Sugestao[] = [
  {
    grupo: 'auditoria',
    label: 'Meus dados estão bons?',
    prompt: 'Meus dados estão bons?',
    intentId: 'auditor.data_quality',
  },
  {
    grupo: 'auditoria',
    label: 'Onde estão os buracos do meu cadastro?',
    prompt: 'Onde estão os buracos do meu cadastro?',
    intentId: 'auditor.gaps',
  }
]

/**
 * ═══ O QUE O ÍCONE ABRE: as duas famílias, nesta ordem ═══
 *
 * Esta é a superfície. `ChatPage` desenha ESTA constante quando `mostrarPartida`
 * está ligado, e não desenha nada quando está desligado — não há segunda porta.
 *
 * A ordem importa em DOIS lugares e é a mesma: indicador primeiro.
 *   - na tela, é a ordem dos grupos do painel;
 *   - no motor, `modules/insights/intents.ts` pega os DOIS PRIMEIROS que ainda
 *     não foram perguntados para sugerir o próximo passo; inverter aqui faria a
 *     auditoria aparecer antes de todo painel.
 *
 * `src/features/artifacts/__tests__/chips-de-sugestao.test.tsx` (do #801) crava
 * sobre esta lista que os oito têm `label === prompt` e que os `intentId` batem,
 * na ordem, com o registro do motor — que pina a ferramenta pelo TEXTO.
 */
export const CHIPS_DE_PARTIDA: readonly Sugestao[] = [
  ...CHIPS_DE_INDICADOR,
  ...CHIPS_DE_AUDITORIA,
]
